import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { motion } from 'framer-motion';
import { Mail, MapPin, Instagram, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import { useData } from '../components/DataContext';
import { Reveal } from '../components/Reveal';
import useScrambleText from '../hooks/useScrambleText';
import ElectricCard from '../components/ElectricCard';

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Invalid email address." }),
  message: z.string().min(10, { message: "Message must be at least 10 characters." }),
});

const Contact = () => {
  const { addMessage } = useData();
  const h2Ref = useScrambleText("Booking & Contact");
  const h3Ref = useScrambleText("Let's Create Magic");
  
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
    },
  });

  function onSubmit(values) {
    addMessage(values);
    toast.success("Message sent to the void!", {
      description: "Korov will receive your transmission shortly.",
      duration: 5000,
    });
    form.reset();
  }

  return (
    <section id="contact" className="py-16 pb-8 bg-black relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-primary/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* Contact Info */}
          <Reveal>
            <h2 ref={h2Ref} className="text-primary font-heading text-sm tracking-[0.3em] uppercase mb-4 cursor-pointer">Booking & Contact</h2>
            <h3 ref={h3Ref} className="text-4xl md:text-5xl font-heading font-bold korov-gradient-text mb-8 cursor-pointer">Let&apos;s Create Magic</h3>
            
            <p className="text-muted-foreground text-lg mb-12 font-body">
              Available for bookings worldwide. Open for collaborations, remixes, and spiritual gatherings.
            </p>

            <div className="space-y-8">
              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center text-primary group-hover:border-primary/50 group-hover:shadow-[0_0_15px_hsl(var(--primary)/0.3)] transition-all duration-300">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-gradient-platinum font-heading group-hover:text-primary transition-colors">Email</h4>
                  <p className="text-muted-foreground">bookings@dharmar.net</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 group">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center text-primary group-hover:border-primary/50 group-hover:shadow-[0_0_15px_hsl(var(--primary)/0.3)] transition-all duration-300">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-gradient-platinum font-heading group-hover:text-primary transition-colors">Base</h4>
                  <p className="text-muted-foreground">Belgrade, Serbia</p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex gap-4">
              <Button variant="outline" size="icon" className="rounded-full border-white/20 hover:bg-primary hover:text-black hover:border-primary hover:shadow-[0_0_15px_hsl(var(--primary))] transition-all duration-300">
                <Instagram className="w-5 h-5" />
              </Button>
              <Button variant="outline" size="icon" className="rounded-full border-white/20 hover:bg-primary hover:text-black hover:border-primary hover:shadow-[0_0_15px_hsl(var(--primary))] transition-all duration-300">
                <Facebook className="w-5 h-5" />
              </Button>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.2}>
            <ElectricCard color="#00ff41">
              <div className="p-8 md:p-10 bg-card/50 backdrop-blur-sm">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gradient-platinum font-heading">Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your name" {...field} className="bg-background/50 border-white/10 focus:border-primary focus:ring-primary/20 text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gradient-platinum font-heading">Email</FormLabel>
                          <FormControl>
                            <Input placeholder="your@email.com" {...field} className="bg-background/50 border-white/10 focus:border-primary focus:ring-primary/20 text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gradient-platinum font-heading">Message</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Tell us about your event..." {...field} className="bg-background/50 border-white/10 focus:border-primary focus:ring-primary/20 min-h-[150px] text-foreground" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full bg-primary text-black hover:bg-primary/80 hover:shadow-[0_0_20px_hsl(var(--primary)/0.4)] font-heading tracking-widest py-6 transition-all duration-300">
                      SEND MESSAGE
                    </Button>
                  </form>
                </Form>
              </div>
            </ElectricCard>
          </Reveal>

        </div>
      </div>
    </section>
  );
};

export default Contact;
